import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2Kpi,
  V2MetricStrip,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { businessDate, daysBetween, systemClock } from '@sakhtar/calendar';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { OpenItem } from '../../lib/types/finance.js';
import { CHARGE_TYPE_LABEL } from '../../lib/types/finance.js';
import {
  formatRialFa,
  journalHref,
  OPEN_ITEM_STATUS_TONE,
  sourceTypeLabel,
  subledgerSourceHref,
} from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { PartyLabel } from './PartyLabel.js';
import { SalesReturnCreateModal } from './SalesReturnCreateModal.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — /t/kimia/finance, finAr / finAp
 *   (open-item drill-down from «اقلام باز» / «فاکتورهای خرید باز»)
 * archetype: TRANSACTION_DETAIL
 * canonical pattern: breadcrumb → title + status/overdue badges → metric strip
 *   (original · settled · open balance · VAT) with a settlement bar → meta card
 *   → invoice-lines card.
 * status: PARTIAL_REAL — GET .../finance/{ar|ap}/open-items/:id. Journal
 *   and source-document links re-check permission on the destination route.
 *   «تسویه‌شده» is original − open balance (BigInt), i.e. the amount already
 *   allocated by treasury settlements. The list of individual allocations is
 *   NOT shown: the API only returns allocations inside the treasury
 *   transaction detail (apps/api/src/finance/subledger-serialize.ts
 *   `serializeTreasuryTransaction`); the open-item detail has no
 *   settlements field and no endpoint filters allocations by open item.
 *   Business dates render Jalali via displayJalaliDate; overdue days use
 *   @sakhtar/calendar `daysBetween` against `systemClock.today()`.
 * state reference: ready · permission_denied · not_found · network_error
 */
export function OpenItemDetailPage({ kind }: { readonly kind: 'ar' | 'ap' }) {
  const { unit, label } = useAmountDisplay();
  const { slug, openItemId } = useParams<{ slug: string; openItemId: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const title = kind === 'ar' ? 'دریافتنی' : 'پرداختنی';
  const listPath = kind === 'ar' ? `${base}/finance/ar` : `${base}/finance/ap`;
  const [showSalesReturn, setShowSalesReturn] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const item = useApiGet<OpenItem>(
    slug && openItemId ? `${base}/api/v1/finance/${kind}/open-items/${openItemId}` : null,
    [slug, kind, openItemId, refreshKey],
  );

  if (!slug || !openItemId) return null;

  // Settled share + overdue days: BigInt for money, calendar helpers for dates.
  let settledRial = '0';
  let settledPercent = 0;
  let overdueDays = 0;
  if (item.status === 'ready') {
    const original = BigInt(item.data.originalAmountRial);
    const open = BigInt(item.data.openBalanceRial);
    const settled = original > open ? original - open : 0n;
    settledRial = settled.toString();
    settledPercent = original > 0n ? Number((settled * 100n) / original) : 0;
    if (item.data.status === 'open' || item.data.status === 'partial') {
      try {
        overdueDays = Math.max(
          0,
          daysBetween(businessDate(item.data.dueDate.slice(0, 10)), systemClock.today()),
        );
      } catch {
        overdueDays = 0;
      }
    }
  }

  const sourceHref =
    item.status === 'ready'
      ? subledgerSourceHref(kind, item.data.sourceType, item.data.sourceReference, base)
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="sk-breadcrumb">
        <span>مالی</span>
        <span>/</span>
        <Link to={listPath}>{title === 'دریافتنی' ? 'دریافتنی‌ها' : 'پرداختنی‌ها'}</Link>
        <span>/</span>
        <b>{title}</b>
      </div>

      {item.status === 'loading' ? <LoadingState /> : null}
      {item.status === 'error' ? (
        <StatePanel
          variant={
            item.error.status === 403
              ? 'permission_denied'
              : item.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            item.error.status === 403
              ? 'دسترسی ندارید'
              : item.error.status === 404
                ? 'پیدا نشد'
                : 'قابل نمایش نیست'
          }
        />
      ) : null}

      {item.status === 'ready' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>
              {title}
              {item.data.sourceReference ? (
                <>
                  {' '}
                  <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                    {item.data.sourceReference}
                  </span>
                </>
              ) : null}
            </h1>
            <V2Badge tone={OPEN_ITEM_STATUS_TONE[item.data.status] ?? 'neutral'}>
              {item.data.statusLabel}
            </V2Badge>
            {overdueDays > 0 ? (
              <V2Badge tone={overdueDays > 90 ? 'danger' : 'warn'}>
                {overdueDays.toLocaleString('fa-IR')} روز گذشته از سررسید
              </V2Badge>
            ) : null}
            <span
              style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}
            >
              {kind === 'ar' && (item.data.status === 'open' || item.data.status === 'partial') && (
                <V2Button
                  variant="secondary"
                  size="sm"
                  icon="invoice"
                  onClick={() => setShowSalesReturn(true)}
                >
                  ثبت برگشت از فروش / بستانکار
                </V2Button>
              )}
              <Link to={`${base}/finance/treasury`}>
                <V2Button variant="secondary" size="sm" icon="plus">
                  {kind === 'ar' ? 'ثبت دریافت' : 'ثبت پرداخت'}
                </V2Button>
              </Link>
            </span>
          </div>
          <V2MetricStrip>
            <V2Kpi
              label="مبلغ اصلی"
              value={`${formatRialFa(item.data.originalAmountRial, unit)} ${label}`}
            />
            <V2Kpi label="تسویه‌شده" value={`${formatRialFa(settledRial, unit)} ${label}`} />
            <V2Kpi
              label="مانده"
              value={`${formatRialFa(item.data.openBalanceRial, unit)} ${label}`}
            />
            <V2Kpi
              label="مالیات"
              value={`${formatRialFa(item.data.vatAmountRial, unit)} ${label}`}
            />
          </V2MetricStrip>
          <div
            className="sk-prog"
            role="progressbar"
            aria-label="درصد تسویه"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={settledPercent}
          >
            <div className="sk-prog__fill" style={{ width: `${settledPercent}%` }} />
          </div>
          <V2Card>
            <div
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 16,
              }}
            >
              <Meta label="طرف‌حساب">
                <PartyLabel
                  slug={slug}
                  partyId={item.data.partyId}
                  href={`${base}/master-data/parties/${item.data.partyId}`}
                />
              </Meta>
              <Meta label="منبع">{sourceTypeLabel(item.data.sourceType)}</Meta>
              <Meta label="شمارهٔ منبع">
                {sourceHref ? (
                  <Link
                    className="ltr sk-num"
                    to={sourceHref}
                    style={{ fontFamily: 'var(--sk-font-mono)' }}
                  >
                    {item.data.sourceReference}
                  </Link>
                ) : (
                  <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                    {item.data.sourceReference || '—'}
                  </span>
                )}
              </Meta>
              <Meta label="تاریخ سند">
                <span className="sk-num">{displayJalaliDate(item.data.businessDate)}</span>
              </Meta>
              <Meta label="سررسید">
                <span className="sk-num">{displayJalaliDate(item.data.dueDate)}</span>
              </Meta>
              <Meta label="سند حسابداری">
                <Link to={journalHref(base, item.data.originatingJournalId)}>مشاهدهٔ سند</Link>
              </Meta>
            </div>
          </V2Card>
          {item.data.lines && item.data.lines.length > 0 ? (
            <V2Card>
              <V2CardHeader title="اقلام فاکتور" />
              <div className="sk-table-scroll">
                <table className="sk-table sk-table--compact">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>شرح</th>
                      <th>مقدار</th>
                      <th>فی ({label})</th>
                      <th>مبلغ ({label})</th>
                      <th>تخفیف ({label})</th>
                      <th>مشمول مالیات ({label})</th>
                      <th>ارزش افزوده ({label})</th>
                      <th>جمع ({label})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.data.lines.map((line) => (
                      <tr key={line.openItemLineId}>
                        <td className="sk-num">{line.rowNumber}</td>
                        <td>
                          {line.lineKind === 'charge' && line.chargeType ? (
                            <V2Badge tone="info">{CHARGE_TYPE_LABEL[line.chargeType]}</V2Badge>
                          ) : null}{' '}
                          {line.description}
                        </td>
                        <td className="sk-num">
                          {formatQuantity(line.quantityNum, line.quantityDen)}
                        </td>
                        <td className="sk-num">{formatRialFa(line.unitPriceRial, unit)}</td>
                        <td className="sk-num">{formatRialFa(line.grossAmountRial, unit)}</td>
                        <td className="sk-num">
                          {formatRialFa(
                            (
                              BigInt(line.lineDiscountRial) + BigInt(line.documentDiscountRial)
                            ).toString(),
                            unit,
                          )}
                        </td>
                        <td className="sk-num">{formatRialFa(line.taxableBaseRial, unit)}</td>
                        <td className="sk-num">{formatRialFa(line.vatAmountRial, unit)}</td>
                        <td className="sk-num">{formatRialFa(line.totalAmountRial, unit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </V2Card>
          ) : null}

          {showSalesReturn && item.status === 'ready' && (
            <SalesReturnCreateModal
              open={showSalesReturn}
              onClose={() => setShowSalesReturn(false)}
              onSuccess={() => {
                setRefreshKey((k) => k + 1);
              }}
              slug={slug}
              base={base}
              unit={unit}
              unitLabel={label}
              initialPartyId={item.data.partyId}
              initialOpenItemId={item.data.openItemId}
              initialInvoiceReference={item.data.sourceReference ?? undefined}
              initialControlAccountId={item.data.controlAccountId ?? undefined}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

function Meta({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--sk-fg)' }}>{children}</span>
    </div>
  );
}

/** Exact decimal quantity from num/den (den is a power of ten), Persian digits. */
function formatQuantity(num: string, den: string): string {
  const scale = den.length - 1;
  if (scale <= 0) return BigInt(num).toLocaleString('fa-IR');
  const padded = num.padStart(scale + 1, '0');
  const whole = padded.slice(0, -scale);
  const fraction = padded.slice(-scale).replace(/0+$/, '');
  const text = fraction
    ? `${BigInt(whole).toLocaleString('fa-IR')}٫${fraction}`
    : BigInt(whole).toLocaleString('fa-IR');
  return text.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]!);
}
